import styles from './styles.module.scss'
import { useParams } from 'react-router-dom'
import { API } from '@Root/API'
import { useEffect, useState, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { snackbarActions } from '@Root/store'
import { errorMessage } from '@Root/helpers'
import { SubmissionDetailHeader } from '@Root/modules/Forms/components/SubmissionDetailHeader'
import { SubmissionInfo } from '@Root/modules/Forms/components/SubmissionInfo'

export const RawSubmissionData = () => {
  const { submissionId } = useParams()
  const [submissionData, setSubmissionData] = useState(null)
  const [rawData, setRawData] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isResyncing, setIsResyncing] = useState(false)
  const dispatch = useDispatch()
  const rawTextareaRef = useRef(null)

  const autoResize = useCallback(ref => {
    const el = ref?.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }, [])

  const formatJSON = jsonString => {
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2)
    } catch (e) {
      return jsonString
    }
  }

  const minifyJSON = jsonString => {
    try {
      return JSON.stringify(JSON.parse(jsonString))
    } catch (e) {
      return jsonString
    }
  }

  const fetchDataHandler = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await API.cognitoForms.getRawSubmissionData(submissionId)
      const data = response.data || response
      const submission = data.data || data

      setSubmissionData(submission)

      if (submission?.raw_data) {
        const rawDataString = typeof submission.raw_data === 'string' ? submission.raw_data : JSON.stringify(submission.raw_data, null, 2)
        setRawData(rawDataString)
      }
    } catch (error) {
      console.error('Error fetching submission data:', error)
      dispatch(snackbarActions.setSnackbar({ text: errorMessage(error), isError: true }))
    } finally {
      setIsLoading(false)
    }
  }, [submissionId, dispatch])

  useEffect(() => {
    fetchDataHandler()
  }, [fetchDataHandler])

  useEffect(() => {
    if (isLoading || !rawData) return
    autoResize(rawTextareaRef)
  }, [rawData, isLoading, autoResize])

  const handleResync = async () => {
    if (isResyncing) return
    setIsResyncing(true)
    try {
      const res = await API.cognitoForms.resyncSubmission(submissionId)
      const result = res.data || res
      if (result.success) {
        dispatch(snackbarActions.setSuccessNotification({ text: 'Resync completed successfully' }))
        fetchDataHandler()
      } else {
        dispatch(snackbarActions.setSnackbar({ text: result.message || 'Resync failed', isError: true }))
      }
    } catch (error) {
      dispatch(snackbarActions.setSnackbar({ text: errorMessage(error), isError: true }))
    } finally {
      setIsResyncing(false)
    }
  }

  if (isLoading) {
    return <div className={styles.loading}>Loading...</div>
  }

  return (
    <div className={styles.wrapper}>
      <SubmissionDetailHeader onResync={handleResync} isResyncing={isResyncing} applicationLink={submissionData?.application_link} />

      <SubmissionInfo data={submissionData} />

      {rawData ? (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Raw Data (Original from Cognito)</h2>
            <div className={styles.buttons}>
              <button className={styles.formatButton} onClick={() => setRawData(formatJSON(rawData))}>
                Format
              </button>
              <button className={styles.formatButton} onClick={() => setRawData(minifyJSON(rawData))}>
                Minify
              </button>
            </div>
          </div>
          <div className={styles.editorContainer}>
            <textarea ref={rawTextareaRef} className={styles.jsonEditor} value={rawData} onChange={e => setRawData(e.target.value)} spellCheck={false} />
          </div>
        </div>
      ) : null}
    </div>
  )
}
